import React, { ReactElement, useState } from 'react';
import { Config } from './types';
import styled from 'styled-components';
import {
  getBorderData,
  getLineColor,
} from 'builder/components/WidgetConfigurations/Border';
import { PaddingData } from 'builder/components/WidgetConfigurations/Padding';
import {
  BackgroundData,
  getBackgroundData,
} from 'builder/components/WidgetConfigurations/Background/Background';
import {
  getBold,
  getCharacterSpacing,
  getFontFamily,
  getFontSize,
  getItalic,
  getLineSpacing,
  getStrikeThrough,
  getTextColor,
  getUnderline,
} from 'builder/components/WidgetConfigurations/Typography/Typography';
import { buttonRadiusMultiplier } from 'builder/util/constants';
import { track } from 'shared/util/analytics/track';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import { Icon } from 'shared/theme/link-ui-react/Icon';

const StyledSpan: any = styled.span<{
  backgroundData: BackgroundData;
  backgroundImage: Image;
  currentTab: number;
  hoverBackgroundData: BackgroundData;
}>`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  background-image: ${p =>
    p.backgroundData?.style === 'Image'
      ? `url('${p => p.backgroundImage?.url}')`
      : ''};
  background: ${p =>
    p.currentTab === 0
      ? p.backgroundData?.color
      : p.hoverBackgroundData?.color};
  background-position: 'center';
  background-repeat: 'no-repeat';
  background-size: 'cover';
`;

export const StyledLink: any = styled.a<{
  font: string;
  fontSize: number;
  lineSpacing: number;
  characterSpacing: number;
  textColor: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikeThrough: boolean;
  borderData: BorderData;
  lineColor: string;
  paddingData: PaddingData;
  currentTab: number;
  hoverPaddingData: PaddingData;
  hover: boolean;
  hoverFontFamily: string;
  hoverBold: boolean;
  hoverTextColor: string;
  hoverFontSize: number;
  hoverItalic: boolean;
  hoverUnderline: boolean;
  hoverStrikeThrough: boolean;
  hoverCharacterSpacing: number;
  hoverLineSpacing: number;
  hoverBorderRadius: number;
  hoverBorderData: BorderData;
  hoverLineColor: string;
  widgetType: string;
}>`
  display: inline-flex;
  font-family: ${p =>
    p.hover ? p.hoverFontFamily || p.font : p.font || 'OptumSans'};
  font-size: ${p => (p.hover ? p.hoverFontSize : p.fontSize)}px;
  font-weight: ${p => {
    if (p.hover) {
      return p.hoverBold ? 'bold' : 'normal';
    }
    return p.bold ? 'bold' : 'normal';
  }};
  font-style: ${p => {
    if (p.hover) {
      return p.hoverItalic ? 'italic' : 'normal';
    }
    return p.italic ? 'italic' : 'normal';
  }};
  text-decoration: ${p => {
    if (p.hover) {
      if (p.hoverUnderline) return 'underline';
      if (p.hoverStrikeThrough) return 'line-through';
      return 'none';
    }
    if (p.underline) return 'underline';
    if (p.strikeThrough) return 'line-through';
    return 'none';
  }};
  line-height: ${p => (p.hover ? p.hoverLineSpacing : p.lineSpacing)};
  letter-spacing: ${p =>
    p.hover ? p.hoverCharacterSpacing : p.characterSpacing}px;
  color: ${p => {
    if (p.hover) {
      return p.hoverTextColor || p.textColor || '';
    }
    if (p.widgetType === 'Link' && !p.textColor) {
      return '#0015fa';
    }
    return p.textColor || '';
  }};
  margin: '0.25rem';
  height: 'fit-content';
  width: 'fit-content';
  border-top: ${p =>
    p.borderData?.isTopBorderVisible ?? true
      ? `${p.borderData?.thickness}px solid ${p.lineColor}`
      : 'none'};
  border-bottom: ${p =>
    p.borderData?.isBottomBorderVisible ?? true
      ? `${p.borderData?.thickness}px solid ${p.lineColor}`
      : 'none'};
  border-left: ${p =>
    p.borderData?.isLeftBorderVisible ?? true
      ? `${p.borderData?.thickness}px solid ${p.lineColor}`
      : 'none'};
  border-right: ${p =>
    p.borderData?.isRightBorderVisible ?? true
      ? `${p.borderData?.thickness}px solid ${p.lineColor}`
      : 'none'};
  padding-top: ${p =>
    p.currentTab == 0 ? p.paddingData?.top : p.hoverPaddingData?.top}px;
  padding-right: ${p =>
    p.currentTab == 0 ? p.paddingData?.right : p.hoverPaddingData?.right}px;
  padding-bottom: ${p =>
    p.currentTab == 0 ? p.paddingData?.bottom : p.hoverPaddingData?.bottom}px;
  padding-left: ${p =>
    p.currentTab == 0 ? p.paddingData?.left : p.hoverPaddingData?.left}px;
  border-radius: ${p =>
    p.currentTab === 0
      ? p.borderData?.radius * buttonRadiusMultiplier
      : p.hoverBorderRadius}px;
  &:focus {
    outline: 3px solid ${p => p.textColor};
    padding: 3px;
    border-radius: 3px;
  }
  &:hover {
    border-top: ${p =>
      p.hoverBorderData?.isTopBorderVisible ?? true
        ? `${p.hoverBorderData?.thickness}px solid ${p.hoverLineColor}`
        : 'none'};
    border-bottom: ${p =>
      p.hoverBorderData?.isBottomBorderVisible ?? true
        ? `${p.hoverBorderData?.thickness}px solid ${p.hoverLineColor}`
        : 'none'};
    border-left: ${p =>
      p.hoverBorderData?.isLeftBorderVisible ?? true
        ? `${p.hoverBorderData?.thickness}px solid ${p.hoverLineColor}`
        : 'none'};
    border-right: ${p =>
      p.hoverBorderData?.isRightBorderVisible ?? true
        ? `${p.hoverBorderData?.thickness}px solid ${p.hoverLineColor}`
        : 'none'};
    padding-top: ${p => p.hoverPaddingData?.top}px;
    padding-bottom: ${p => p.hoverPaddingData?.right}px;
    padding-left: ${p => p.hoverPaddingData?.left}px;
    padding-right: ${p => p.hoverPaddingData?.bottom}px;
    color: ${p => p.hoverTextColor};
    border-radius: ${p => p.hoverBorderRadius}px;
  }
`;

export const StyledDiv = styled.div`
  padding: 0 0.25rem;
`;

const LinkWidget: React.FC<WidgetRenderProps<Config>> = (
  props: WidgetRenderProps<Config>
): ReactElement => {
  const { editing, widgetState, currentPage, site } = props;
  const {
    displayText,
    paddingData,
    url,
    emailAddress,
    emailSubject,
    linkType,
    newTab,
    iconColor,
    backgroundImage,
    currentTab,
    bold,
    visible,
    isCustomWidget,
    analyticsLinkText,
  } = props.widgetState.config;
  const { hoverData } = props.widgetState.config;
  const [hover, setHover] = useState(false);
  // Standardized tracking hook
  const trackLinkWidget = useTrackWidget({
    tracking: props.tracking,
    widgetState,
    currentPage,
    site,
  });

  if (visible === false || isCustomWidget) {
    return <></>;
  }

  const href =
    linkType === 'Email' && emailAddress
      ? `mailto:${emailAddress}${
          emailSubject ? `?subject=${encodeURIComponent(emailSubject)}` : ''
        }`
      : url;

  function sendLinkClickMetrics() {
    trackLinkWidget('link_widget_click', {
      linkWidgetLabel: analyticsLinkText ?? displayText,
      linkWidgetPage: currentPage.slug,
      linkWidgetSiteName: site.domain,
      linkUrl: href,
    });
  }

  return (
    <StyledSpan
      backgroundData={getBackgroundData(widgetState, currentTab)}
      backgroundImage={backgroundImage}
      currentTab={currentTab ?? 0}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      hoverBackgroundData={hoverData?.backgroundData}
    >
      <StyledLink
        data-test-id="styled-link"
        href={editing || href === '' ? null : href}
        target={newTab ? '_blank' : ''}
        font={getFontFamily(widgetState, currentTab)}
        fontSize={getFontSize(widgetState, currentTab)}
        textColor={getTextColor(widgetState, currentTab)}
        characterSpacing={getCharacterSpacing(widgetState, currentTab)}
        lineSpacing={getLineSpacing(widgetState, currentTab)}
        strikeThrough={getStrikeThrough(widgetState, currentTab)}
        italic={getItalic(widgetState, currentTab)}
        bold={getBold(widgetState, currentTab)}
        underline={getUnderline(widgetState, currentTab)}
        currentTab={currentTab ?? 0}
        borderData={getBorderData(widgetState, currentTab)}
        lineColor={getLineColor(widgetState, currentTab)}
        paddingData={paddingData}
        hover={hover}
        hoverFontFamily={hoverData?.font}
        hoverBold={hoverData?.bold}
        hoverTextColor={hoverData?.textColor}
        hoverFontSize={hoverData?.fontSize}
        hoverItalic={hoverData?.italic}
        hoverUnderline={hoverData?.underline}
        hoverStrikeThrough={hoverData?.strikeThrough}
        hoverCharacterSpacing={hoverData?.characterSpacing}
        hoverLineSpacing={hoverData?.lineSpacing}
        hoverPaddingData={hoverData?.paddingData}
        hoverBorderRadius={
          hoverData?.borderData?.radius * buttonRadiusMultiplier
        }
        hoverBorderData={hoverData?.borderData}
        hoverLineColor={hoverData?.lineColor}
        widgetType={widgetState.type}
        onClick={() => {
          !editing && sendLinkClickMetrics();
        }}
      >
        {bold ? <strong>{displayText}</strong> : displayText}
        {newTab && (
          <StyledDiv>
            <Icon
              data-test-id="open-in-new-tab-icon"
              icon="NewWindowDarkArrow"
              width={'1em'}
              height={'.9em'}
              viewBox="0 0 15 15"
              style={{ fill: `${iconColor}` }}
              aria-label="Opens in new window/tab"
            />
          </StyledDiv>
        )}
      </StyledLink>
    </StyledSpan>
  );
};
export default track()(LinkWidget);
